import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import firebaseApi from "../apis/firebaseApi";
import dateHelpers from "../helpers/dateHelpers";
import fontHelpers from "../helpers/fontHelpers";
import RouteEnum from "../routes/RouteEnum";

let FrameContext = React.createContext();

const FrameContextProvider = (props) => {
  let session = {}; // sessionStorage.getItem("session") ? JSON.parse(sessionStorage.getItem("session")) : {};
  const [source, setSource] = React.useState(session.source);
  const [companyId, setCompanyId] = React.useState(session.companyId);
  const [locationId, setLocationId] = React.useState(session.locationId);
  const [agentId, setAgentId] = React.useState(session.agentId);
  const [brandId, setBrandId] = React.useState(session.brandId);
  const [currentUserId, setCurrentUserId] = React.useState(session.currentUserId);
  const [appointmentData, setAppointmentData] = React.useState();
  const [user, setUser] = React.useState();
  const [settings, setSettings] = React.useState();
  const [refresh, setRefresh] = React.useState(false);
  let history = useHistory();

  React.useEffect(() => {
    const unsub = history.listen((location, action) => {
      if (action === "POP" && location && location.pathname === "/") {
        sendMessage("scheduling_canceled");
      }
    });
    return () => unsub();
  }, [history]);

  React.useEffect(async () => {
    let urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("source") && urlParams.has("companyId")) {
      let data = {
        source: urlParams.get("source"),
        companyId: urlParams.get("companyId"),
        locationId: urlParams.get("locationId"),
        brandId: urlParams.get("brandId") || urlParams.get("subsiteId"),
        agentId: urlParams.get("agentId"),
        currentUserId: urlParams.get("currentUserId"),
      };
      if (urlParams.has("company.name")) {
        data.company = { name: urlParams.get("company.name") };
      }
      if (urlParams.has("agent.name") || urlParams.has("agent.email") || urlParams.has("agent.timeZoneOffset")) {
        data.agent = {};
        if (urlParams.has("agent.name")) {
          data.agent.name = urlParams.get("agent.name");
        }
        if (urlParams.has("agent.email")) {
          data.agent.email = urlParams.get("agent.email");
        }
        if (urlParams.has("agent.timeZoneOffset")) {
          data.agent.timeZoneOffset = urlParams.get("agent.timeZoneOffset");
        }
      }
      if (urlParams.has("location.name") || urlParams.has("location.email") || urlParams.has("location.timeZoneOffset")) {
        data.location = {};
        if (urlParams.has("location.name")) {
          data.location.name = urlParams.get("location.name");
        }
        if (urlParams.has("location.email")) {
          data.location.email = urlParams.get("location.email");
        }
        if (urlParams.has("location.timeZoneOffset")) {
          data.location.timeZoneOffset = urlParams.get("location.timeZoneOffset");
        }
      }
      if (urlParams.has("lang")) {
        data.settings = { lang: urlParams.get("lang") };
      }

      await init(data);
    }
  }, []);

  const init = async (data) => {
    const supportedSources = ["connect", "inside"];
    if (supportedSources.indexOf(data.source) < 0) return;
    if (data.subsiteId) {
      data.brandId = data.subsiteId;
    }
    setSource(data);
    var lang = data.settings && data.settings.lang ? data.settings.lang : data.user && data.user.lang ? data.user.lang : "en";
    if (data.user) setUser({ ...data.user, lang: lang, gmtOffset: dateHelpers.getTimeZoneOffset() });
    if (data.user && data.user.lang) data.settings.lang = data.user.lang;
    setSettings(data.settings);
    setCurrentUserId(data.currentUserId);
    setAppointmentData(data.appointmentData);
    //sessionStorage.setItem("session", JSON.stringify(data));
    let companyDoc = await firebaseApi.getCompany(data.source, data.companyId, data.company);
    let companyId = companyDoc.id;
    setCompanyId(companyId);

    let locationId = null;
    let hasBrandCss = false;
    if (data.locationId) {
      let locationDoc = await firebaseApi.getOrCreateLocation(companyId, data.source, parseInt(data.locationId), data.location);
      let locationId = locationDoc.id;
      setLocationId(locationId);
    }

    if (data.agentId) {
      if (locationId) {
        data.agent = data.agent || {};
        data.agent.locationId = locationId;
      }
      let agentDoc = await firebaseApi.getOrCreateAgent(companyId, data.source, data.agentId.toString(), data.agent);
      let agentId = agentDoc.id;
      setAgentId(agentId);
    }

    if (data.brandId) {
      let brandDoc = await firebaseApi.getOrCreateBrand(companyId, data.source, data.brandId);
      let brandId = brandDoc.id;
      setBrandId(brandId);
      if (brandDoc.data() && brandDoc.data().customCss) {
        fontHelpers.addCustomCss(brandDoc.data().customCss);
        hasBrandCss = true;
      }
    }

    if (data.settings && data.settings.styles && data.settings.styles.fonts) {
      if (Array.isArray(data.settings.styles.fonts)) {
        for (let i = 0; i < data.settings.styles.fonts.length; i++) {
          fontHelpers.addCustomFont(data.settings.styles.fonts[i], i === data.settings.styles.fonts.length - 1);
        }
      } else {
        fontHelpers.addCustomFont(data.settings.styles.fonts, true);
      }
    }

    if (!hasBrandCss && companyDoc.data() && companyDoc.data().customCss) {
      fontHelpers.addCustomCss(companyDoc.data().customCss);
    }

    if (data.settings && data.settings.styles && data.settings.styles.customCss) {
      fontHelpers.addCustomCss(data.settings.styles.customCss);
    }
  };

  const navigateToPage = useCallback(
    (page) => {
      if (history.location.pathname === RouteEnum[page]) {
        setRefresh((r) => !r);
      } else {
        history.push(RouteEnum[page]);
      }
    },
    [history]
  );

  const handleWindowMessage = useCallback(
    (message) => {
      if (!message.data) return;
      let data = typeof message.data === "string" ? JSON.parse(message.data) : message.data;

      if (/^react-devtools/gi.test(data.source)) return;
      if (!data || (data.type !== "PfEvent" && data.type !== "CTSEvent")) return;
      switch (data.name) {
        case "scheduling_init":
        case "switchboard_init":
          init(data.data);
          if (data.page) {
            navigateToPage(data.page);
          }
          break;
        case "scheduling_back":
        case "switchboard_back":
          history.goBack();
          break;
        case "scheduling_navigate":
        case "switchboard_navigate":
          navigateToPage(data.page);
          break;
        default:
          break;
      }
    },
    [history, navigateToPage]
  );

  React.useEffect(() => {
    window.addEventListener("message", handleWindowMessage, false);

    return () => {
      window.removeEventListener("message", handleWindowMessage, false);
    };
  }, [handleWindowMessage]);

  const isInFrame = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  const sendMessage = (name, data) => {
    window.parent.postMessage({ type: "PfEvent", name, data }, "*");
  };

  return (
    <FrameContext.Provider
      value={{ source, companyId, locationId, agentId, brandId, currentUserId, appointmentData, user, sendMessage, isInFrame, refresh, settings }}
    >
      {props.children}
    </FrameContext.Provider>
  );
};

export { FrameContext, FrameContextProvider };
